import type { AppConfigMasteryLevelsType } from "@memorang/constants/config/schema";
import { isAttempted } from "@memorang/helpers/item";
import {
	type ProficiencyBandFill,
	calculateBandFillPercents,
	getHighestCompletedBandIndex,
	sumItemPoints,
} from "@memorang/helpers/language-learning";
import type { QuestionItem } from "@memorang/helpers/report";
import { isProficiencyTag } from "@memorang/helpers/tag";
import type { BaseItemSchema, Item, MixedItemTag } from "@memorang/types";
import type { LevelIconName } from "../../../icon";
import type { ConnectedLevel } from "../../ConnectedLevels";
import { flattenSessionItemsForLookup } from "./helpers";

const FALLBACK_PROFICIENCY_ICON: LevelIconName = "circle-outline";

export const hasProficiencyProgressUi = ({
	tags,
	sessionItems,
	proficiencyLevels,
}: {
	tags: MixedItemTag[];
	sessionItems: BaseItemSchema[];
	proficiencyLevels?: AppConfigMasteryLevelsType;
}): boolean => {
	if (!proficiencyLevels?.length) {
		return false;
	}
	if (tags.some((tag) => isProficiencyTag(tag))) {
		return true;
	}
	return flattenSessionItemsForLookup(sessionItems).some((item) =>
		Boolean(item.metadata?.proficiency?.trim()),
	);
};

export const buildProficiencyBandItemIds = ({
	proficiencyTags,
	sessionItems,
	proficiencyLevels,
}: {
	proficiencyTags: MixedItemTag[];
	sessionItems: BaseItemSchema[];
	proficiencyLevels: AppConfigMasteryLevelsType;
}): Map<string, Set<number>> => {
	const bandIds = new Map<string, Set<number>>();
	for (const level of proficiencyLevels) {
		bandIds.set(level.alias.title.toLowerCase(), new Set());
	}

	const tags = proficiencyTags.filter((tag) => isProficiencyTag(tag));
	if (tags.length > 0) {
		for (const tag of tags) {
			const key = tag.title.toLowerCase();
			const set = bandIds.get(key) ?? new Set<number>();
			for (const item of tag.items ?? []) {
				set.add(item.id);
			}
			bandIds.set(key, set);
		}
		return bandIds;
	}

	for (const sessionItem of flattenSessionItemsForLookup(sessionItems)) {
		const label = sessionItem.metadata?.proficiency?.trim();
		if (!label || sessionItem.id == null) {
			continue;
		}
		const key = label.toLowerCase();
		const set = bandIds.get(key);
		if (!set) {
			continue;
		}
		set.add(sessionItem.id);
	}
	return bandIds;
};

/** Prefer report proficiency tags; otherwise synthesize bands from item metadata. */
export const resolveSessionProficiencyTags = ({
	tags,
	sessionItems,
	proficiencyLevels,
}: {
	tags: MixedItemTag[];
	sessionItems: BaseItemSchema[];
	proficiencyLevels?: AppConfigMasteryLevelsType;
}): MixedItemTag[] => {
	const proficiencyTags = tags.filter((tag) => isProficiencyTag(tag));
	if (proficiencyTags.length > 0) {
		return proficiencyTags;
	}
	if (!proficiencyLevels?.length) {
		return [];
	}

	const bandIds = buildProficiencyBandItemIds({
		proficiencyTags: [],
		sessionItems,
		proficiencyLevels,
	});

	const itemsById = new Map<number, Item>();
	for (const tag of tags) {
		for (const item of tag.items ?? []) {
			itemsById.set(item.id, item);
		}
	}

	return [...proficiencyLevels]
		.sort((a, b) => a.sortOrder - b.sortOrder)
		.map((level, index) => {
			const ids = bandIds.get(level.alias.title.toLowerCase()) ?? new Set();
			const items = [...ids].flatMap((id) => {
				const item = itemsById.get(id);
				return item ? [item] : [];
			});
			return {
				id: -(index + 1),
				title: level.alias.title,
				type: "Proficiency",
				sortOrder: level.sortOrder,
				distribution: {
					high: 0,
					medium: 0,
					low: 0,
					none: 0,
				},
				numItems: items.length,
				totalResponses: 0,
				items,
			} satisfies MixedItemTag;
		});
};

export const buildProficiencyBandFillsForQuestionType = ({
	questionItems,
	proficiencyTags,
	sessionItems,
	proficiencyLevels,
	targetScore,
}: {
	questionItems: QuestionItem[];
	proficiencyTags: MixedItemTag[];
	sessionItems: BaseItemSchema[];
	proficiencyLevels: AppConfigMasteryLevelsType;
	targetScore: number;
}): ProficiencyBandFill[] => {
	const bandIds = buildProficiencyBandItemIds({
		proficiencyTags,
		sessionItems,
		proficiencyLevels,
	});
	const orderedLevels = [...proficiencyLevels].sort(
		(a, b) => a.sortOrder - b.sortOrder,
	);

	const bands = orderedLevels.map((level) => {
		const label = level.alias.title;
		const ids = bandIds.get(label.toLowerCase()) ?? new Set<number>();
		const overlap = questionItems.filter((item) => ids.has(item.id));
		const { numPointsEarned, numPointsTotal } = sumItemPoints(overlap);
		const answeredCount = overlap.filter(isAttempted).length;
		return {
			label,
			numPointsEarned,
			numPointsTotal,
			answeredCount,
			availableCount: overlap.length,
			hasItems: answeredCount > 0,
			hasAvailableItems: overlap.length > 0,
		};
	});
	return calculateBandFillPercents(bands, targetScore);
};

export const mapFillsToConnectedLevels = (
	fills: ProficiencyBandFill[],
	iconMap?: Record<string, LevelIconName>,
): ConnectedLevel[] =>
	fills.map((fill) => ({
		label: fill.label,
		icon: iconMap?.[fill.label] ?? FALLBACK_PROFICIENCY_ICON,
		progress: fill.percent,
	}));

/**
 * Session-local estimated level for one question type.
 */
export const getSessionLevelForQuestionType = (
	fills: ProficiencyBandFill[],
): string | undefined => {
	if (!fills.some((fill) => fill.hasItems)) {
		return undefined;
	}
	const highestCompletedIndex = getHighestCompletedBandIndex(fills);
	const completedFill =
		highestCompletedIndex >= 0 ? fills[highestCompletedIndex] : undefined;
	return completedFill?.label ?? fills[0]?.label;
};
